import { getGeoLocationCode } from "../../../externalApi/DashboardHostApiInternal";
import { addMarkerToPageTimings } from "../../../../telemetry/Measures";
import { dashboard } from "../../../../telemetry/TraceLogging";
import { AppType, PageType } from "../../../constants";
import { requestClearCache } from "../../../externalApi/DashboardHostApi";
import { getPageCompositionServiceCache, refreshFeedsContentAsync, tryGetBackgroundTaskStatus, tryGetFeedsContent } from "../../../externalApi/FeedsApi";
import { isFeatureEnabled, isFeatureEnabledInternal } from "../../../externalApi/VelocityApi";
import { getIsWindowVisible } from "../../../externalApi/WindowApi";
import { lazyLoadModule } from "../../../lazyLoadModule";
import { BackgroundTaskStatus, RefreshContentTaskStatus, WidgetForceRefreshUnify1SCallVerb, } from "../../../widgetRuntime/apis/api.interfaces";
import { getWidgetOrchestrator } from "../../../widgetRuntime/orchestrator/orchestrator";
import { getWidgetGroupAttrs } from "../../WidgetGroup/WidgetGroup";
const TelemetryModule = import(
/* webpackChunkName: "Telemetry-Chunk" */
"../../../../telemetry/Telemetry");
window.__com_microsoft_dsh_widget_promises = {};
function getOverrideParams() {
    const overrideParams = {};
    const searchParams = new URLSearchParams(location.search);
    searchParams.forEach((value, key) => {
        if (key === "item") {
            const itemPair = decodeURIComponent(value).split(":");
            const itemKey = itemPair[0];
            const itemValue = itemPair[1];
            if (itemPair && itemPair.length === 2 && itemKey && itemValue) {
                overrideParams.pcsAdditionalQsp = overrideParams.pcsAdditionalQsp
                    ? `${overrideParams.pcsAdditionalQsp}&item=${value}`
                    : `item=${value}`;
            }
        }
    });
    return overrideParams;
}
async function getWidgetAttributes() {
    const dataWidgetAttributes = (await getWidgetGroupAttrs({
        appType: AppType,
        pageType: PageType,
        pcsAdditionalQsp: "",
    })) || {};
    const overrideParams = getOverrideParams();
    const queryStringParams = [];
    if (overrideParams.pcsAdditionalQsp) {
        queryStringParams.push(overrideParams.pcsAdditionalQsp);
    }
    if (dataWidgetAttributes.pcsAdditionalQsp) {
        queryStringParams.push(dataWidgetAttributes.pcsAdditionalQsp);
    }
    if (queryStringParams.length > 0) {
        dataWidgetAttributes.pcsAdditionalQsp = queryStringParams.join("&");
    }
    return dataWidgetAttributes;
}
async function buildPcsFetchUrl() {
    const dataWidgetAttributes = await getWidgetAttributes();
    const { appType, useCnDomain, pageType, pcsAdditionalQsp } = dataWidgetAttributes;
    return `https://www.msn.${useCnDomain ? "cn" : "com"}/pcs/api/widget/${appType || AppType}/${pageType || PageType}${pcsAdditionalQsp ? `?${pcsAdditionalQsp}` : ""}`;
}
function preloadBundle(bundleUrl) {
    if (!bundleUrl) {
        return;
    }
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "script";
    link.crossOrigin = "crossorigin";
    link.href = bundleUrl;
    document.head.appendChild(link);
}
// Singleton instance
let g_fetchWidgetIntializer;
export function fetchWidgetIntializer() {
    if (!g_fetchWidgetIntializer) {
        g_fetchWidgetIntializer = new Promise(async (resolve, reject) => {
            let url;
            if ((await isFeatureEnabledInternal("Feature_PreDownloadInitializer"))) {
                const widgetInitializerDomain = getGeoLocationCode() === "CN" ? "https://assets.msn.cn" : "https://assets.msn.com";
                url = widgetInitializerDomain + "/bundles/v1/" + AppType + "/latest/widget-initializer.js";
            }
            else {
                const orchestrator = await getWidgetOrchestrator();
                url = orchestrator.getWidgetInitializerUrl(AppType);
            }
            const initializer = await import(/* webpackIgnore: true */ url).catch((error) => {
                // Importing widget-initializer.js failed.
                // If this is due to WV2 cache issue, try clearing it to recover.
                requestClearCache();
                reject(error);
            });
            resolve(initializer);
        });
    }
    return g_fetchWidgetIntializer;
}
const timeOutInMs = 15000;
export function prefetchPCSAndCoreBundles() {
    return new Promise(async (resolve, reject) => {
        var _a;
        addMarkerToPageTimings("MEE.PrefetchPCSStart");
        let pcsResponse = null;
        const startTimestamp = Date.now();
        let getCachedPcsTimestamp;
        let getWebPcsTimestamp;
        const pcsJSON = await getPageCompositionServiceCache();
        if (pcsJSON) {
            pcsResponse = JSON.parse(pcsJSON);
            getCachedPcsTimestamp = Date.now();
        }
        if (!pcsResponse) {
            const pcsUrl = await buildPcsFetchUrl();
            let timer;
            const response = await Promise.race([
                fetch(pcsUrl, { credentials: "include" }),
                new Promise((resolve) => (timer = setTimeout(() => resolve(null), timeOutInMs))),
            ]).catch(reject);
            if (timer) {
                clearTimeout(timer);
                timer = null;
            }
            getWebPcsTimestamp = Date.now();
            if (!response || !response.ok) {
                reject(null);
            }
            else {
                pcsResponse = await response.json();
            }
        }
        lazyLoadModule(TelemetryModule).then((Telemetry) => {
            Telemetry.logEvent(dashboard, "WebAppPerfMeasure", 16777216 /* productAndServicePerformance */, {
                type: "prefetchPCSAndCoreBundles",
                startTimestamp,
                getCachedPcsTimestamp,
                getWebPcsTimestamp
            });
        });
        addMarkerToPageTimings("MEE.PrefetchPCSEnd");
        if (!pcsResponse) {
            reject(null);
        }
        else {
            resolve(pcsResponse);
            // only prg-prljs can preload core js bundle
            const flightInfo = ((_a = pcsResponse === null || pcsResponse === void 0 ? void 0 : pcsResponse.pcsData) === null || _a === void 0 ? void 0 : _a.info) || "";
            if (flightInfo) {
                const flightString = ((/f:\s*([^;]+)/i.exec(flightInfo) || {})[1] || "").toLowerCase();
                const flightsSet = new Set(flightString.split(","));
                if (flightsSet.has("prg-prljs")) {
                    for (const scriptName in pcsResponse.bundleData.bootstrappingRefs) {
                        const scriptUrl = scriptName && pcsResponse.bundleData.bootstrappingRefs[scriptName];
                        preloadBundle(scriptUrl);
                    }
                }
            }
        }
    });
}
async function getGeoLocationStrForUnify1SCall() {
    const widgetModelApiFactory = window.__com_microsoft_dsh_widgetModelApiFactory;
    if (widgetModelApiFactory) {
        const geolocationApi = widgetModelApiFactory.tryGetApi("geolocation", 1, 1);
        if (geolocationApi) {
            const geolocation = await geolocationApi.getLocation();
            // Parameters needed for device location
            if (geolocation) {
                const clientLocationExists = geolocation.latitude && geolocation.longitude;
                const clientLocationProviderExists = geolocation.positionSource && geolocation.positionSource.length > 0;
                const clientLocationTimestampExists = geolocation.timestamp && geolocation.timestamp.length > 0;
                const geoLocationServiceEnabled = clientLocationExists && clientLocationProviderExists && clientLocationTimestampExists && geolocation.accuracy;
                if (geoLocationServiceEnabled) {
                    const clientLocation = geolocation.latitude + "|" + geolocation.longitude;
                    const clientLocationAccuracy = geolocation.accuracy.toString();
                    const clientLocationProvider = geolocation.positionSource;
                    const clientLocationTimestamp = geolocation.timestamp;
                    return `clientLocation=${clientLocation}&clientLocationAccuracy=${clientLocationAccuracy}&clientLocationProvider=${clientLocationProvider}&clientLocationTimestamp=${clientLocationTimestamp}`;
                }
            }
        }
    }
    return "";
}
export function prefetchFeed() {
    return new Promise(async (resolve, reject) => {
        addMarkerToPageTimings("MEE.PrefetchFeedStart");
        let shellResponse;
        const startTimestamp = Date.now();
        let refreshStartTimestamp;
        let refreshEndTimestamp;
        try {
            const checkBackgroundTaskAndFetchContentFromClient = async () => {
                let response;
                let backgroundTaskStatus = BackgroundTaskStatus.Disabled;
                backgroundTaskStatus = (await tryGetBackgroundTaskStatus()).toString();
                if (backgroundTaskStatus !== BackgroundTaskStatus.Disabled) {
                    const shellJson = await tryGetFeedsContent();
                    if (shellJson) {
                        response = JSON.parse(shellJson);
                    }
                }
                return response;
            };
            shellResponse = await checkBackgroundTaskAndFetchContentFromClient();
            // if no cached response, try to forceRefresh and get the latest response
            if (!shellResponse) {
                const refreshPromise = new Promise(async (refreshResolve, refreshReject) => {
                    try {
                        refreshStartTimestamp = Date.now();
                        const [isVisible, geolocationParam] = await Promise.all([getIsWindowVisible(), getGeoLocationStrForUnify1SCall()]);
                        const callerParam = isVisible ? "caller=api" : "caller=bgRefresh";
                        await refreshFeedsContentAsync(WidgetForceRefreshUnify1SCallVerb.modifyUrl, `${callerParam}&${geolocationParam}`, (status) => {
                            refreshEndTimestamp = Date.now();
                            return refreshResolve(status);
                        });
                    }
                    catch (error) {
                        return refreshReject(error);
                    }
                });
                const status = await refreshPromise;
                if (RefreshContentTaskStatus.Succeed === status) {
                    shellResponse = await checkBackgroundTaskAndFetchContentFromClient();
                }
            }
        }
        catch (e) {
            reject(e);
        }
        lazyLoadModule(TelemetryModule).then((Telemetry) => {
            Telemetry.logEvent(dashboard, "WebAppPerfMeasure", 16777216 /* productAndServicePerformance */, {
                type: "PrefetchFeedContent",
                startTimestamp,
                refreshStartTimestamp,
                refreshEndTimestamp
            });
        });
        addMarkerToPageTimings("MEE.PrefetchFeedEnd");
        resolve(shellResponse);
    });
}
export async function bootstrapPrefetchTask() {
    // try to fetch widget-initializer.js instantly so that we can reuse it later
    fetchWidgetIntializer();
    const [prefetchFeedFeatureEnabled, prefetchPCSEnabled] = await Promise.all([
        isFeatureEnabled("Feature_PrefetchFeedContent"),
        isFeatureEnabled("Feature_PrefetchPCS")
    ]);
    // only Feature_PrefetchPCS feature is enabled can we prefetch pcs
    if (prefetchPCSEnabled) {
        window.__com_microsoft_dsh_widget_promises["prefetchPcsInfo"] = prefetchPCSAndCoreBundles();
    }
    if (prefetchFeedFeatureEnabled) {
        window.__com_microsoft_dsh_widget_promises["prefetchContent"] = prefetchFeed();
    }
}
